/**
 * Общие примитивы разделов «Аналитики», наполняемых выпусками БНС
 * («Торговля» US-30/US-31, «Строительство» US-23/US-24, «Сельское
 * хозяйство» US-17/US-18) — загрузка блока, подписи периодов, вывод
 * величин и ИФО.
 *
 * Три правила экрана, из-за которых величины не печатаются как обычные
 * числа:
 *  - **ИФО экран не рассчитывает никогда** (US-31 FR-8.3, US-24 FR-3.4):
 *    методика источника своя — по СКО номинальное отношение объёмов даёт
 *    127,2 % при публикуемом ИФО 118,4 %. Индекс либо пришёл из ответа,
 *    либо не показывается; сверху он не ограничен (391,3 %, 897,4 % —
 *    фактические значения выпусков, US-23 FR-6.5).
 *  - **Единицу и формат объявляет ответ** (US-31 FR-7.1, US-24 FR-3.1):
 *    экран единицы не пересчитывает и по названию показателя не
 *    подставляет — один и тот же показатель разные регионы публикуют в
 *    разных единицах.
 *  - **Состояний значения шесть** (US-31 FR-9, US-24 FR-5): «нет данных»,
 *    «-» (явление отсутствует), «х» (конфиденциально), «…» (нет в
 *    выпуске), «0,0» (незначительная величина) и настоящий нуль — и ни
 *    одно из них не сводится к нулю или к прочерку.
 *
 * Расчётные величины (US-31 FR-8.2) помечаются знаком с формулой из
 * ответа: арифметика платформы не выдаётся за публикуемую статистику.
 */
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useKato } from '../../../contexts/KatoContext';
import { useYear } from '../../../contexts/YearContext';
import { cn } from '../../../lib/cn';
import type {
  StatIfo,
  StatPeriod,
  StatSource,
  StatValue,
} from '../../../types/analytics';

export const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

export const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

export const fmt2 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v);

/**
 * Ровно один знак после запятой — там, где постановка требует именно
 * такой точности: объём продукции и ИФО сельского хозяйства публикуются
 * с одним знаком, и «105 %» вместо «105,0 %» читается как другая
 * точность источника (US-17 FR-2.2, FR-2.3).
 */
export const fmt1Fixed = (v: number): string =>
  Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(v);

interface BlockState<T> {
  data: T | null;
  loading: boolean;
}

/**
 * Загрузка одного блока страницы: плашка и блоки запрашиваются
 * независимо (US-30 §НФТ, US-23 §НФТ) — годовой или квартальный выпуск
 * не задерживает месячную плашку, а его недоступность не обнуляет
 * остальные блоки.
 */
export const useStatBlock = <T,>(
  fetcher: (kato: number, year?: number | null) => Promise<T>,
  /** false — блока на экране нет (выключен, сводная витрина): не грузим. */
  enabled = true,
): BlockState<T> => {
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();
  const [state, setState] = useState<BlockState<T>>({
    data: null,
    loading: false,
  });

  useEffect(() => {
    if (selectedKato === null || !enabled) {
      setState({ data: null, loading: false });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true });
    fetcher(selectedKato, selectedYear)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false });
      })
      .catch((err) => {
        // Блок без данных рисует «нет данных», а не ошибку интерфейса.
        console.error('useStatBlock failed', err);
        if (!cancelled) setState({ data: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, selectedKato, selectedYear, enabled]);

  return state;
};

/** Строка происхождения величины для подсказки (US-31 FR-7.5, US-24 FR-4). */
export const sourceHint = (source: StatSource | null): string | undefined => {
  if (source === null) return undefined;
  return [source.name, source.issue, source.published_at]
    .filter((part): part is string => part !== null)
    .join(' · ');
};

/** Значение с состоянием: печатается текст состояния, а не ноль (US-24 FR-5). */
export const useStatText = () => {
  const { t } = useTranslation();

  return (
    value: StatValue | null,
    format: (v: number) => string = fmt1,
  ): { text: string; known: boolean; hint: string | undefined } => {
    if (value === null) {
      return {
        text: t('analytics.stats.states.noData'),
        known: false,
        hint: undefined,
      };
    }
    if (value.state === 'value' && value.value !== null) {
      return {
        text: format(value.value),
        known: true,
        hint: sourceHint(value.source),
      };
    }
    // «0,0» — незначительная величина: печатается как в источнике,
    // но остаётся отличимой от нуля и от отсутствия (FR-9.5).
    if (value.state === 'negligible') {
      return {
        text: t('analytics.stats.states.negligible'),
        known: false,
        hint: t('analytics.stats.hints.negligible'),
      };
    }
    const key =
      value.state === 'absent'
        ? 'absent'
        : value.state === 'confidential'
          ? 'confidential'
          : value.state === 'not_published'
            ? 'notPublished'
            : value.state === 'not_applicable'
              ? 'notApplicable'
              : 'noData';
    return {
      text: t(`analytics.stats.states.${key}`),
      known: false,
      hint: t(`analytics.stats.hints.${key}`),
    };
  };
};

interface TradeNumberProps {
  value: StatValue | null;
  format?: (v: number) => string;
  /** Единица рядом со значением (из ответа — экран её не домысливает). */
  withUnit?: boolean;
  className?: string;
  testId?: string;
}

/** Величина ответа: значение либо текст своего состояния (US-24 FR-5). */
export const StatNumber = ({
  value,
  format = fmt1,
  withUnit = false,
  className,
  testId,
}: TradeNumberProps) => {
  const text = useStatText();
  const shown = text(value, format);
  const hint = [shown.hint, value?.calculated === true ? value.formula : null]
    .filter((part): part is string => part != null && part !== '')
    .join(' · ');

  return (
    <span
      className={cn(
        'tabular-nums',
        shown.known ? 'font-extrabold' : 'text-sm font-normal text-text-faint',
        className,
      )}
      title={hint === '' ? undefined : hint}
      data-testid={testId}
    >
      {shown.text}
      {withUnit && shown.known && value?.unit != null && (
        <span className="ml-1 text-sm font-normal text-text-muted">
          {value.unit}
        </span>
      )}
      {shown.known && value?.calculated === true && <CalculatedMark />}
    </span>
  );
};

/**
 * Пометка расчётной величины (US-31 FR-8.2): экран не выдаёт арифметику
 * платформы за публикуемые данные источника.
 */
export const CalculatedMark = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  return (
    <sup
      className={cn('ml-0.5 text-xs font-normal text-text-faint', className)}
      title={t('analytics.stats.calculatedHint')}
      data-testid="stat-calculated"
    >
      {t('analytics.stats.calculated')}
    </sup>
  );
};

/**
 * ИФО: значение источника в процентах, без ограничения сверху (US-31
 * FR-7.4, US-23 FR-6.5) — 654,9 %, 897,4 % и 52,3 % встречаются в
 * выпусках. Стрелка — по значению относительно 100 %, а «рост — это
 * хорошо или плохо» берётся из `direction` ответа и в вёрстку не
 * зашивается (US-31 FR-8.4, US-24 FR-3.7).
 */
export const StatIfoValue = ({
  ifo,
  withBase = false,
  className,
  testId,
}: {
  ifo: StatIfo | null;
  withBase?: boolean;
  className?: string;
  testId?: string;
}) => {
  const { t } = useTranslation();
  const text = useStatText();

  if (ifo === null) return null;

  const shown = text(
    {
      value: ifo.value,
      state: ifo.state,
      unit: null,
      calculated: false,
      formula: null,
      source: ifo.source,
    },
    fmt1,
  );

  if (!shown.known) {
    return (
      <span
        className={cn('text-sm text-text-faint', className)}
        title={shown.hint}
        data-testid={testId}
      >
        {t('analytics.stats.ifoShort')} {shown.text}
      </span>
    );
  }

  const grew = (ifo.value ?? 0) >= 100;
  const good = ifo.direction === 'up_is_bad' ? !grew : grew;
  const Arrow = grew ? ArrowUp : ArrowDown;

  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1 whitespace-nowrap font-bold tabular-nums',
        ifo.direction === null
          ? 'text-text'
          : good
            ? 'text-brand-600'
            : 'text-status-danger',
        className,
      )}
      title={sourceHint(ifo.source)}
      data-testid={testId}
    >
      {shown.text}%
      <Arrow className="size-3.5 shrink-0 self-center" aria-hidden="true" />
      {withBase && ifo.base_label !== null && (
        <span className="text-sm font-normal text-text-muted">
          {ifo.base_label}
        </span>
      )}
    </span>
  );
};

/**
 * Отчётная подпись блока (US-31 FR-6.1, US-24 FR-2): у плашки и блоков
 * периоды разные, и общей подписи у страницы нет. Отставание выпуска —
 * отдельный признак, а не «нет данных» (US-23 FR-5.5).
 */
export const PeriodCaption = ({
  period,
  prefix,
  suffix,
  className,
  testId,
}: {
  period: StatPeriod | null;
  /** Название модуля перед периодом: «Сельское хозяйство · 2026 (…)». */
  prefix?: string | null;
  /** Единица блока и прочие уточнения — из ответа. */
  suffix?: string | null;
  /** Например `min-w-0 shrink` — длинная подпись переносится, а не
   * выдавливает заголовок блока за край на планшете. */
  className?: string;
  testId?: string;
}) => {
  const { t } = useTranslation();
  if (period === null && prefix == null && suffix == null) return null;

  const parts = [prefix ?? null, period?.label ?? null, suffix ?? null].filter(
    (part): part is string => part !== null && part !== '',
  );

  return (
    <span
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-2 text-sm text-text-faint',
        className,
      )}
      data-testid={testId}
    >
      {parts.join(' · ')}
      {/* Предварительный год выпуска (US-47 FR-4.3): молча показанная
          предварительная цифра неотличима от окончательной. */}
      {period?.preliminary === true && (
        <span
          className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-semibold text-text-muted"
          data-testid="stat-period-preliminary"
        >
          {t('analytics.stats.preliminary')}
        </span>
      )}
      {period?.lagging === true && (
        <span
          className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-semibold text-text-muted"
          data-testid="stat-period-lagging"
          title={t('analytics.stats.laggingHint')}
        >
          {t('analytics.stats.lagging')}
        </span>
      )}
    </span>
  );
};

/** Подпись источника блока (US-31 FR-7.5, US-24 FR-4.1). */
export const SourceNote = ({ source }: { source: StatSource | null }) => {
  const { t } = useTranslation();
  const hint = sourceHint(source);
  if (hint === undefined) return null;
  return (
    <p className="mt-3 text-xs leading-snug text-text-faint">
      {t('analytics.stats.sourceLabel')}: {hint}
    </p>
  );
};

/** «Нет данных» блока — явное состояние, а не пустая рамка (US-23 FR-7.6). */
export const StatNoData = ({
  text,
  testId = 'block-placeholder',
}: {
  text: string;
  testId?: string;
}) => (
  <p data-testid={testId} className="text-sm lowercase text-text-muted">
    {text}
  </p>
);
